import { listAdmissions } from "@/core/admissions/admission-store";
import { TRIAGE_LABELS } from "@/core/admissions/types";
import { listAppointmentsByPatient } from "@/core/appointments/appointment-store";
import {
  listConsultationsByPatient,
  listPrescriptionsByPatient,
} from "@/core/clinical/clinical-store";
import { formatPrescriptionPosology, formatVitalsSummary } from "@/core/clinical/types";
import { listLabOrdersByPatient } from "@/core/laboratory/lab-store";
import { formatLabResultSummary } from "@/core/laboratory/types";
import { listStaysByPatient } from "@/core/hospitalization/hospitalization-store";
import { STAY_STATUS_LABELS } from "@/core/hospitalization/types";
import { listImagingExamsByPatient } from "@/core/imaging/imaging-store";
import { formatImagingReportSummary } from "@/core/imaging/types";
import { listVitalSignRecordsByPatient } from "@/core/nursing/vital-sign-store";
import {
  formatVitalSignRecordSummary,
  VITAL_SIGN_CONTEXT_LABELS,
} from "@/core/nursing/types";
import { listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import { patientFullName } from "@/core/patients/types";
import type { Patient } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

export type MedicalRecordEventType =
  | "admission"
  | "appointment"
  | "consultation"
  | "prescription"
  | "lab"
  | "imaging"
  | "vital_sign"
  | "hospitalization"
  | "invoice";

export interface MedicalRecordEvent {
  id: string;
  type: MedicalRecordEventType;
  date: string;
  title: string;
  summary: string;
  statusLabel?: string;
  linkPath?: string;
}

const TYPE_LABELS: Record<MedicalRecordEventType, string> = {
  admission: "Admission",
  appointment: "Rendez-vous",
  consultation: "Consultation",
  prescription: "Prescription",
  lab: "Laboratoire",
  imaging: "Imagerie",
  vital_sign: "Constantes infirmier",
  hospitalization: "Hospitalisation",
  invoice: "Facture pharmacie",
};

export function medicalRecordTypeLabel(type: MedicalRecordEventType): string {
  return TYPE_LABELS[type];
}

export function buildPatientMedicalTimeline(patient: Patient): MedicalRecordEvent[] {
  const events: MedicalRecordEvent[] = [];

  for (const admission of listAdmissions().filter((a) => a.patientId === patient.id)) {
    const isEmergency = admission.channel === "emergency";
    const triagePart =
      isEmergency && admission.triageLevel
        ? `${TRIAGE_LABELS[admission.triageLevel]}${admission.glasgowScore != null ? ` · Glasgow ${admission.glasgowScore}` : ""}`
        : null;
    events.push({
      id: admission.id,
      type: "admission",
      date: admission.createdAt,
      title: isEmergency
        ? `Urgences — ${admission.motif || "Sans motif"}`
        : `Admission — ${admission.motif || "Sans motif"}`,
      summary: triagePart
        ? triagePart
        : admission.destination
          ? `Orienté vers ${admission.destination}`
          : `Statut : ${admission.status}`,
      statusLabel: admission.status,
      linkPath: isEmergency ? routes.emergency : routes.admissions,
    });
  }

  for (const appt of listAppointmentsByPatient(patient.id)) {
    events.push({
      id: appt.id,
      type: "appointment",
      date: appt.scheduledAt,
      title: `${appt.serviceUnit} — ${appt.motif}`,
      summary: appt.practitioner
        ? `Avec ${appt.practitioner} · ${appt.durationMinutes} min`
        : "Médecin non assigné",
      statusLabel: appt.status,
    });
  }

  for (const con of listConsultationsByPatient(patient.id)) {
    const vitalsSummary = formatVitalsSummary(con.vitals);
    events.push({
      id: con.id,
      type: "consultation",
      date: con.createdAt,
      title: con.motif,
      summary: vitalsSummary
        ? `${con.diagnostic || "Diagnostic en cours"} · ${vitalsSummary}`
        : con.diagnostic || "Diagnostic en cours",
      statusLabel: con.status,
      linkPath: `/tenant/hospitol/clinical/consultations/${con.id}`,
    });
  }

  for (const rx of listPrescriptionsByPatient(patient.id)) {
    events.push({
      id: rx.id,
      type: "prescription",
      date: rx.createdAt,
      title: `Ordonnance (${rx.lines.length} ligne${rx.lines.length !== 1 ? "s" : ""})`,
      summary:
        rx.lines.map((l) => `${l.productLabel}: ${formatPrescriptionPosology(l)}`).join(" · ") ||
        "—",
      statusLabel: rx.status,
      linkPath: "/tenant/hospitol/clinical/prescriptions",
    });
  }

  for (const lab of listLabOrdersByPatient(patient.id)) {
    const prescriber = lab.prescriber ? `Prescrit par ${lab.prescriber}` : "Prescripteur non renseigné";
    const consultation = lab.consultationId ? " · Liée à une consultation" : "";
    events.push({
      id: lab.id,
      type: "lab",
      date: lab.createdAt,
      title: lab.testName,
      summary:
        lab.status === "completed"
          ? formatLabResultSummary(lab)
          : `${prescriber}${consultation}${lab.clinicalNotes ? ` · ${lab.clinicalNotes}` : ""}`,
      statusLabel: lab.status,
      linkPath: "/tenant/hospitol/laboratory/results",
    });
  }

  for (const record of listVitalSignRecordsByPatient(patient.id)) {
    events.push({
      id: record.id,
      type: "vital_sign",
      date: record.recordedAt,
      title: `Relevé infirmier — ${VITAL_SIGN_CONTEXT_LABELS[record.context]}`,
      summary: formatVitalSignRecordSummary(record),
      statusLabel: record.validatedAt ? "Validé" : "En attente",
      linkPath: routes.nursingCare,
    });
  }

  for (const stay of listStaysByPatient(patient.id)) {
    events.push({
      id: stay.id,
      type: "hospitalization",
      date: stay.admittedAt,
      title: `Séjour — ${stay.wardName}`,
      summary: stay.motif || stay.attendingPractitioner || "Hospitalisation",
      statusLabel: STAY_STATUS_LABELS[stay.status],
      linkPath: routes.hospitalization,
    });
  }

  for (const exam of listImagingExamsByPatient(patient.id)) {
    events.push({
      id: exam.id,
      type: "imaging",
      date: exam.completedAt ?? exam.scheduledAt,
      title: `${exam.examType}${exam.bodyPart ? ` — ${exam.bodyPart}` : ""}`,
      summary:
        exam.status === "completed"
          ? formatImagingReportSummary(exam)
          : `${exam.prescriber || "Prescripteur non renseigné"}${exam.clinicalIndication ? ` · ${exam.clinicalIndication}` : ""}`,
      statusLabel: exam.status,
      linkPath: routes.imagingReports,
    });
  }

  const nameLower = patientFullName(patient).toLowerCase();
  for (const inv of listValidatedInvoices()) {
    if (inv.patientId !== patient.id && inv.customer.name.trim().toLowerCase() !== nameLower) {
      continue;
    }
    events.push({
      id: inv.id,
      type: "invoice",
      date: inv.validatedAt,
      title: `Facture ${inv.number}`,
      summary: `${inv.lines.length} article(s)`,
      linkPath: "/tenant/hospitol/pharmacy/invoices",
    });
  }

  return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function countPatientMedicalEvents(patientId: string): number {
  return (
    listAdmissions().filter((a) => a.patientId === patientId).length +
    listAppointmentsByPatient(patientId).length +
    listConsultationsByPatient(patientId).length +
    listPrescriptionsByPatient(patientId).length +
    listLabOrdersByPatient(patientId).length +
    listVitalSignRecordsByPatient(patientId).length +
    listStaysByPatient(patientId).length +
    listImagingExamsByPatient(patientId).length
  );
}
